import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DollarSign, Loader2, Search, Check, AlertTriangle, Clock, Ban,
  Gift, Calendar, Pencil, X, Shield, TrendingUp
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const STATUS_CONFIG = {
  active_paid: { label: 'Active Paid', color: '#00CC99', icon: Check },
  trial_active: { label: 'Trial Active', color: '#8B5CF6', icon: Clock },
  free_until_date: { label: 'Free Until Date', color: '#3B82F6', icon: Calendar },
  complimentary: { label: 'Complimentary', color: '#EC4899', icon: Gift },
  payment_due: { label: 'Payment Due', color: '#F59E0B', icon: AlertTriangle },
  suspended: { label: 'Suspended', color: '#EF4444', icon: Ban },
};

/**
 * Iter 175g — Master Dashboard consolidation.
 *
 * "Billing Controls" used to be its own sidebar tab (`/master/billing/controls`).
 * It now lives as a section inside the merged Billing tab (`/master/billing`).
 * Pass `embedded` (default true) to skip the page-level <h1> so MasterBilling
 * can render this block right under its own header without two stacked titles.
 */
export default function MasterBillingControls({ embedded = true }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingSite, setEditingSite] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/master/billing/controls`, { headers })
      .then(res => setSites(res.data || []))
      .catch(() => toast.error('Failed to load billing'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = sites.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.site_name?.toLowerCase().includes(q) || s.owner_email?.toLowerCase().includes(q);
  });

  const handleSave = async () => {
    if (!editingSite) return;
    setSaving(true);
    try {
      await axios.put(`${API}/master/billing/controls/${editingSite.site_id}`, {
        status: editingSite.billing_status,
        plan: editingSite.plan,
        trial_end: editingSite.trial_end,
        free_until: editingSite.free_until,
        monthly_rate: editingSite.monthly_rate,
        discount_percent: editingSite.discount_percent,
        notes: editingSite.notes,
      }, { headers });
      setSites(prev => prev.map(s => s.site_id === editingSite.site_id ? editingSite : s));
      setEditingSite(null);
      toast.success('Billing updated');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const statusCounts = sites.reduce((acc, s) => { acc[s.billing_status] = (acc[s.billing_status] || 0) + 1; return acc; }, {});

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-controls-page">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-white">Billing Controls</h1>
          <p className="text-sm text-slate-400 mt-1">Manage pricing, trials, and billing status for all client sites</p>
        </div>
      )}
      {embedded && (
        <div>
          <h2 className="text-xl font-bold text-white font-syne flex items-center gap-2">
            <Shield size={18} className="text-[#A78BFA]" /> Pricing &amp; Trial Overrides
          </h2>
          <p className="text-sm text-slate-400 mt-1">Manually adjust monthly rate, trial dates, complimentary status, and discounts for any site.</p>
        </div>
      )}

      {/* Status Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <GlassCard key={key} className="p-4 text-center">
              <Icon size={18} className="mx-auto mb-1.5" style={{ color: cfg.color }} />
              <p className="text-xl font-bold text-white">{statusCounts[key] || 0}</p>
              <p className="text-[10px] text-slate-500">{cfg.label}</p>
            </GlassCard>
          );
        })}
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sites..." className="pl-10 bg-white/5 border-white/10 text-white rounded-xl" data-testid="billing-search" />
      </div>

      {/* Sites Table */}
      <GlassCard className="overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="billing-table">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
            <th className="px-5 py-3 font-medium">Site</th>
            <th className="px-5 py-3 font-medium">Owner</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium">Plan</th>
            <th className="px-5 py-3 font-medium">Rate</th>
            <th className="px-5 py-3 font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(site => {
              const cfg = STATUS_CONFIG[site.billing_status] || STATUS_CONFIG.trial_active;
              const Icon = cfg.icon;
              return (
                <tr key={site.site_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`billing-row-${site.site_id}`}>
                  <td className="px-5 py-3.5"><p className="text-white font-medium">{site.site_name}</p></td>
                  <td className="px-5 py-3.5 text-slate-400 text-xs">{site.owner_email}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium" style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                      <Icon size={10} /> {cfg.label}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400 text-xs capitalize">{site.plan?.replace('_', ' ')}</td>
                  <td className="px-5 py-3.5 text-white text-xs">{site.monthly_rate ? `$${site.monthly_rate}/mo` : 'Free'}</td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => setEditingSite({ ...site })} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-[#8B5CF6]/30 transition-colors" data-testid={`edit-billing-${site.site_id}`}>
                      <Pencil size={11} /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </GlassCard>

      {/* Edit Dialog */}
      <Dialog open={!!editingSite} onOpenChange={() => setEditingSite(null)}>
        <DialogContent className="bg-[#0E0E23] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Shield size={18} className="text-[#A78BFA]" /> Edit Billing — {editingSite?.site_name}</DialogTitle></DialogHeader>
          {editingSite && (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-slate-400 text-xs">Billing Status</Label>
                <select value={editingSite.billing_status} onChange={(e) => setEditingSite(s => ({ ...s, billing_status: e.target.value }))} className="w-full bg-[#1E1E3A] border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm mt-1" data-testid="edit-billing-status">
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs">Monthly Rate ($)</Label>
                  <Input type="number" value={editingSite.monthly_rate || 0} onChange={(e) => setEditingSite(s => ({ ...s, monthly_rate: Number(e.target.value) }))} className="bg-white/5 border-white/10 text-white rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Discount %</Label>
                  <Input type="number" value={editingSite.discount_percent || 0} onChange={(e) => setEditingSite(s => ({ ...s, discount_percent: Number(e.target.value) }))} className="bg-white/5 border-white/10 text-white rounded-xl mt-1" />
                </div>
              </div>
              {editingSite.billing_status === 'trial_active' && (
                <div>
                  <Label className="text-slate-400 text-xs">Trial End Date</Label>
                  <Input type="date" value={editingSite.trial_end?.split('T')[0] || ''} onChange={(e) => setEditingSite(s => ({ ...s, trial_end: e.target.value }))} className="bg-white/5 border-white/10 text-white rounded-xl mt-1" />
                </div>
              )}
              {editingSite.billing_status === 'free_until_date' && (
                <div>
                  <Label className="text-slate-400 text-xs">Free Until Date</Label>
                  <Input type="date" value={editingSite.free_until?.split('T')[0] || ''} onChange={(e) => setEditingSite(s => ({ ...s, free_until: e.target.value }))} className="bg-white/5 border-white/10 text-white rounded-xl mt-1" data-testid="free-until-input" />
                </div>
              )}
              <div>
                <Label className="text-slate-400 text-xs">Notes</Label>
                <Textarea value={editingSite.notes || ''} onChange={(e) => setEditingSite(s => ({ ...s, notes: e.target.value }))} rows={2} placeholder="Internal notes..." className="bg-white/5 border-white/10 text-white rounded-xl mt-1 resize-none" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setEditingSite(null)} className="flex-1 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-slate-300">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="save-billing-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
